// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Context
import { useCountry } from '@utmb/contexts/CountryContext';
// Modules
import { ListboxOption } from '@reach/listbox';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Input } from '@utmb/design-system/input/Input';
import { InputTel } from '@utmb/design-system/input-tel/InputTel';
import { Select } from '@utmb/design-system/select/Select';
import { InputMessage } from '@utmb/design-system/input-message/InputMessage';
// Types
import { RegistrationSubFormProps } from '@utmb/types/registration-form';
import CountryView from '@utmb/types/view/CountryView';
// Styles
import styles from './user-information.module.scss';

export const OrganizerForm = (props: RegistrationSubFormProps): ReactElement => {
    const { formik } = props;

    const [phoneErrorMessage, setPhoneErrorMessage] = useState('');

    const { t } = useTranslation();

    const { countries, initCountries } = useCountry();

    useEffect(() => {
        initCountries();
    }, []);

    return (
        <>
            <div className={`${styles.row} row`}>
                <div className="col-12 col-md-12">
                    <Input
                        id="name"
                        name="name"
                        type="text"
                        label={t('myutmb:myUtmb.registration.organizer-name')}
                        placeholder={t('myutmb:myUtmb.registration.organizer-name')}
                        value={formik.values.name}
                        onchange={formik.handleChange}
                        onblur={formik.handleBlur}
                        require={true}
                    />
                    {formik.touched.name && formik.errors.name && <InputMessage type="error" message={formik.errors.name} />}
                </div>
                <div className="col-12 col-md-6">
                    <Input
                        id="city"
                        name="city"
                        type="text"
                        label={t('myutmb:myUtmb.registration.city')}
                        placeholder={t('myutmb:myUtmb.registration.city')}
                        value={formik.values.city}
                        onchange={formik.handleChange}
                        onblur={formik.handleBlur}
                        require={true}
                    />
                    {formik.touched.city && formik.errors.city && <InputMessage type="error" message={formik.errors.city} />}
                </div>
            </div>
            <div className={`${styles.row} row`}>
                <div className="col-12 col-md-6">
                    <Select
                        id="countryCode"
                        name="countryCode"
                        label={t('myutmb:myUtmb.myInformation.contact.country')}
                        firstOptionlabel={t('common.selectCountry')}
                        onchange={(value: string) => {
                            formik.getFieldHelpers('countryCode').setValue(value);
                        }}
                        value={formik.values.countryCode}
                        require={true}
                    >
                        {countries?.map((country: CountryView, key: number) => (
                            <ListboxOption key={`communicationLanguage_${key}`} value={country.code} label={country.name}>
                                <Font.FuturaBook as="span" mobile="14" desktop="16">
                                    {country.name}
                                </Font.FuturaBook>
                            </ListboxOption>
                        ))}
                    </Select>
                    {formik.touched.countryCode && formik.errors.countryCode && <InputMessage type="error" message={formik.errors.countryCode} />}
                </div>
                <div className="col-12 col-md-6">
                    <InputTel
                        id="phone"
                        name="phone"
                        label={t('myutmb:myUtmb.myInformation.contact.phone')}
                        value={formik.values.phone}
                        onChange={(fullNumber: string, value: string, isValid: boolean) => {
                            formik.setFieldValue('phone', fullNumber);
                            setPhoneErrorMessage(!isValid ? t('form.telNotValidError') : '');
                        }}
                        onBlur={() => {
                            formik.setFieldTouched('phone', true);
                        }}
                        required={true}
                    />
                    {formik.touched.phone && (formik.errors.phone || !!phoneErrorMessage.length) && (
                        <InputMessage type="error" message={formik.errors.phone || phoneErrorMessage} />
                    )}
                </div>
            </div>
        </>
    );
};
